import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2 } from "lucide-react";

interface CancelCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedulingRequestId: string;
  connectionId: string;
  otherPersonName: string;
  otherPersonId: string;
  scheduledAt: string | null;
  onCancelled: () => void;
}

export function CancelCallDialog({
  open,
  onOpenChange,
  schedulingRequestId,
  connectionId,
  otherPersonName,
  otherPersonId,
  scheduledAt,
  onCancelled,
}: CancelCallDialogProps) {
  const { user } = useAuth();
  const [reason, setReason] = useState("");
  const [wantsReschedule, setWantsReschedule] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidReason = reason.trim().length >= 20;

  const handleCancel = async () => {
    if (!user || !isValidReason) return;

    setIsSubmitting(true);

    try {
      // Update scheduling_requests with cancellation info
      const { error: updateError } = await supabase
        .from("scheduling_requests")
        .update({
          cancelled_at: new Date().toISOString(),
          cancelled_by: user.id,
          cancellation_reason: reason.trim(),
          status: "cancelled",
        })
        .eq("id", schedulingRequestId);

      if (updateError) throw updateError;

      // Clear scheduled_at from connections table
      const { error: connectionError } = await supabase
        .from("connections")
        .update({ scheduled_at: null })
        .eq("id", connectionId);

      if (connectionError) throw connectionError;

      // Send notification emails
      const { error: notifyError } = await supabase.functions.invoke(
        "notify-call-cancelled",
        {
          body: {
            schedulingRequestId,
            cancelledByUserId: user.id,
            cancellationReason: reason.trim(),
            scheduledAt,
          },
        }
      );

      if (notifyError) {
        console.error("Error sending cancellation emails:", notifyError);
        // Don't throw - cancellation succeeded, just email failed
      }

      // If user wants to reschedule, trigger new scheduling email
      if (wantsReschedule) {
        // Get current user's profile for the scheduling email
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user.id)
          .single();

        const { data: otherProfile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", otherPersonId)
          .single();

        const { data: otherPrivate } = await supabase
          .from("profile_private")
          .select("email")
          .eq("user_id", otherPersonId)
          .single();

        if (otherPrivate?.email && profile?.full_name && otherProfile?.full_name) {
          await supabase.functions.invoke("send-scheduling-email", {
            body: {
              connectionId,
              requesterId: user.id,
              recipientId: otherPersonId,
              requesterName: profile.full_name,
              recipientEmail: otherPrivate.email,
              recipientName: otherProfile.full_name,
            },
          });
          toast.success("Call cancelled and reschedule request sent!");
        } else {
          toast.success("Call cancelled. You can reschedule from your connections page.");
        }
      } else {
        toast.success("Call cancelled successfully.");
      }

      setReason("");
      setWantsReschedule(false);
      onOpenChange(false);
      onCancelled();
    } catch (error: any) {
      console.error("Error cancelling call:", error);
      toast.error("Failed to cancel call. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Cancel Scheduled Call
          </DialogTitle>
          <DialogDescription>
            You are about to cancel your call with <strong>{otherPersonName}</strong>.
            {scheduledAt && (
              <span className="block mt-1">
                Scheduled for:{" "}
                {new Date(scheduledAt).toLocaleString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason">
              Please explain why you need to cancel{" "}
              <span className="text-muted-foreground">(minimum 20 characters)</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="I need to cancel because..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {reason.trim().length}/20 characters
              {!isValidReason && reason.length > 0 && (
                <span className="text-destructive ml-2">
                  ({20 - reason.trim().length} more needed)
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="reschedule"
              checked={wantsReschedule}
              onCheckedChange={(checked) => setWantsReschedule(checked === true)}
            />
            <Label htmlFor="reschedule" className="text-sm font-normal cursor-pointer">
              I would like to request a new time
            </Label>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Keep Call
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={!isValidReason || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cancelling...
              </>
            ) : (
              "Cancel Call"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
